/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { Link, useParams } from 'react-router-dom'

import { Alert, Button, ConfirmModal, PageContainer, PageHeader, Toast } from '@/components/ui'
import { useApi } from '@/hooks'
import useToast from '@/hooks/useToast'
import { REGISTERED_SLUGS } from '@/insights/insightCatalogue'
import logger from '@/utils/logger'

import type { AdminAssetGroupDetail, AdminAssetGroupProfile } from './types'

import { LoadingPanel } from '../LoadingPanel'
import {
  APP_GRANTS,
  ConfirmDisableModal,
  PermissionsEditor,
  UnsavedChangesNote,
  applyCap,
  buildUsage,
  disabledInUse,
  normaliseValue,
  registeredInsights,
  usePermissionsForm,
  useSaveConfirm,
  usersAndDashboardsLabel,
  type PermissionsCap,
  type PermissionsValue,
} from '../permissions'

const log = logger({ category: 'AdminAssetGroupDetailPage' })

const plural = (n: number, word: string) => `${String(n)} ${word}${n === 1 ? '' : 's'}`

/**
 * One asset group's permissions (MEGA-776): what its users may have, within what the
 * company allows. Unlike the company page there is no "apply to users" — a group only
 * limits; each user's own lists stay as they are. With no profile saved the group
 * passes its company's lists through, which is what the editor starts from.
 */
const AdminAssetGroupDetailPage: React.FC = () => {
  const { api } = useApi()
  const { companyId = '' } = useParams<{ companyId: string }>()
  const { toast, show, showToast, hideToast } = useToast()

  const [detail, setDetail] = useState<AdminAssetGroupDetail | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [saveError, setSaveError] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [confirmingReset, setConfirmingReset] = useState(false)

  useEffect(() => {
    if (!companyId) return
    let cancelled = false
    const load = async () => {
      setIsLoading(true)
      try {
        const response = await api.get<AdminAssetGroupDetail>({
          path: `/app/admin/asset-groups/${encodeURIComponent(companyId)}`,
          origin: 'AdminAssetGroupDetailPage#load',
        })
        if (cancelled) return
        if (response.error || !response.data) {
          setError(response.message || 'Could not load this asset group')
          return
        }
        setError('')
        setDetail(response.data)
      } catch (ex) {
        log.error(ex)
        if (!cancelled) setError('Could not load this asset group')
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [api, companyId])

  // The company is the ceiling: what it has off can't be ticked here.
  const cap = useMemo<PermissionsCap | null>(() => {
    if (!detail) return null
    const { customerProfile, group } = detail
    return {
      grants: customerProfile.grants,
      insights: customerProfile.insights,
      hasProfile: customerProfile.updatedAt !== null,
      companyName: group.customerName,
    }
  }, [detail])

  // Saved lists, or — with no profile — what passes through: the company's lists, or
  // everything when the company caps nothing. Either way, never more than the ceiling.
  const baseline = useMemo<PermissionsValue | null>(() => {
    if (!detail) return null
    const { profile, customerProfile } = detail
    const source: PermissionsValue =
      profile.updatedAt !== null
        ? profile
        : customerProfile.updatedAt !== null
          ? customerProfile
          : { grants: [...APP_GRANTS], insights: [...REGISTERED_SLUGS] }
    return applyCap({ grants: source.grants, insights: registeredInsights(source.insights) }, cap).value
  }, [cap, detail])

  const { value, setValue, isDirty, reset, commit } = usePermissionsForm<PermissionsValue>({
    baseline,
    normalise: normaliseValue,
  })
  const { pending, request, confirm, cancel } = useSaveConfirm()
  const form = value ?? baseline
  const usage = useMemo(() => buildUsage(detail?.users ?? []), [detail])

  const stored = (profile: AdminAssetGroupProfile) =>
    setDetail((previous) => (previous ? { ...previous, profile } : previous))

  const save = useCallback(async () => {
    if (!form) return
    setIsSaving(true)
    setSaveError('')
    try {
      const response = await api.put<AdminAssetGroupProfile, PermissionsValue>({
        path: `/app/admin/asset-groups/${encodeURIComponent(companyId)}/profile`,
        origin: 'AdminAssetGroupDetailPage#save',
        body: { grants: form.grants, insights: form.insights },
      })
      if (response.error || !response.data) {
        setSaveError(response.message || 'Could not save')
        return
      }
      commit(form)
      stored(response.data)
      showToast({
        header: 'Asset group permissions saved',
        body: 'Changes apply at each user’s next sign-in or token refresh.',
        position: 'end',
        success: true,
      })
    } catch (ex) {
      log.error(ex)
      setSaveError('Could not save')
    } finally {
      setIsSaving(false)
    }
  }, [api, commit, companyId, form, showToast])

  const resetToCompany = useCallback(async () => {
    setConfirmingReset(false)
    setIsSaving(true)
    setSaveError('')
    try {
      const response = await api.remove<AdminAssetGroupProfile>({
        path: `/app/admin/asset-groups/${encodeURIComponent(companyId)}/profile`,
        origin: 'AdminAssetGroupDetailPage#reset',
      })
      if (response.error || !response.data) {
        setSaveError(response.message || 'Could not reset')
        return
      }
      stored(response.data)
      showToast({
        header: 'Asset group reset',
        body: 'Its users get the company’s apps and insights again.',
        position: 'end',
        success: true,
      })
    } catch (ex) {
      log.error(ex)
      setSaveError('Could not reset')
    } finally {
      setIsSaving(false)
    }
  }, [api, companyId, showToast])

  const handleSave = () => {
    if (!baseline || !form) return
    request(disabledInUse(baseline, form, usage), () => void save())
  }

  if (isLoading) {
    return (
      <PageContainer>
        <LoadingPanel label="Loading asset group…" />
      </PageContainer>
    )
  }

  if (error || !detail || !form) {
    return (
      <PageContainer>
        <Link to="/admin/asset-groups" className="link link-hover mb-2 inline-block text-sm">
          ← Asset Groups
        </Link>
        <Alert message={error || 'Asset group not found'} type="error" />
      </PageContainer>
    )
  }

  const { group, assetCount, userCount, profile } = detail
  const hasProfile = profile.updatedAt !== null

  return (
    <PageContainer>
      <Link to="/admin/asset-groups" className="link link-hover mb-2 inline-block text-sm">
        ← Asset Groups
      </Link>
      <PageHeader
        title={group.name}
        subtitle={`${group.customerName} · ${plural(assetCount, 'asset')} · ${plural(userCount, 'user')}`}
      />

      <section className="card max-w-3xl bg-base-100 shadow">
        <div className="card-body">
          <h2 className="card-title text-base">Permissions</h2>
          <p className="text-sm text-base-content/70">
            Users in this asset group can have at most these, within what{' '}
            <Link className="link" to={`/admin/companies/${String(group.customerId)}`}>
              {group.customerName}
            </Link>{' '}
            allows. A user in several groups gets everything any of them allows. Each user&rsquo;s own settings are not
            changed.
          </p>
          <p className="text-sm text-base-content/70" data-testid="profile-state">
            {hasProfile
              ? `Saved ${new Date(profile.updatedAt ?? '').toLocaleDateString()}.`
              : `No settings saved — users get ${group.customerName}’s apps and insights. Saving narrows them.`}
          </p>

          <PermissionsEditor
            value={form}
            onChange={(next) => setValue(next)}
            cap={cap}
            usage={usage}
            inUseLabel={usersAndDashboardsLabel}
            disabled={isSaving}
          />

          {saveError && <Alert message={saveError} type="error" />}

          <div className="card-actions mt-4 items-center justify-end">
            {hasProfile && (
              <Button
                handleClick={() => setConfirmingReset(true)}
                isDisabled={isSaving}
                variation="ghost"
                className="mr-auto"
              >
                Reset to company
              </Button>
            )}
            <Button handleClick={() => reset()} isDisabled={!isDirty || isSaving} variation="ghost">
              Cancel
            </Button>
            <Button handleClick={handleSave} isDisabled={!isDirty || isSaving} variation="primary">
              {isSaving ? (
                <>
                  <span className="loading loading-spinner loading-xs" />
                  Saving…
                </>
              ) : (
                'Save permissions'
              )}
            </Button>
          </div>
          <UnsavedChangesNote show={isDirty} />

          <ConfirmDisableModal
            disabled={pending}
            usage={usage}
            inUseLabel={usersAndDashboardsLabel}
            isSaving={isSaving}
            onConfirm={confirm}
            onCancel={cancel}
          />
          {confirmingReset && (
            <ConfirmModal
              show
              title="Reset to company?"
              confirmLabel="Reset"
              cancelLabel="Keep"
              onConfirm={() => void resetToCompany()}
              onCancel={() => setConfirmingReset(false)}
              body={
                <p className="text-sm">
                  This removes {group.name}&rsquo;s settings. Its {plural(userCount, 'user')} will get{' '}
                  {group.customerName}&rsquo;s apps and insights again.
                </p>
              }
            />
          )}
        </div>
      </section>

      <Toast
        show={show}
        onClose={hideToast}
        header={toast.header}
        body={toast.body}
        position={toast.position}
        success={toast.success}
        info={toast.info}
        warning={toast.warning}
      />
    </PageContainer>
  )
}

export default AdminAssetGroupDetailPage
